// apps/web/src/pages/Public/MagicLink/components/signatureProvider.ts
//
// Ponto ÚNICO de decisão de qual provider de assinatura renderizar.
// Nenhum outro arquivo deve inspecionar URL/host para inferir provider.

/** Providers de assinatura suportados pelo embed do frontend. */
export type SignatureProviderId = 'clicksign' | 'zapsign';

/**
 * Dados do registro de assinatura devolvidos pelo backend
 * (`POST /p/:token/signature/initiate` e `GET /p/:token/signature/status`, ou o
 * initiate do contrato de first-access).
 *
 * `provider` é o campo autoritativo. O backend ainda NÃO o expõe nesses DTOs —
 * enquanto isso, a resolução cai no host do `envelopeUrl`. Quando o campo
 * existir, basta passá-lo aqui: ele tem precedência e o fallback vira inerte.
 */
export interface SignatureEmbedSource {
  /** Provider gravado no registro de assinatura, quando o backend o expõe. */
  provider?: string | null;
  /** URL pronta do envelope/documento (ZapSign: página de assinatura do signatário). */
  envelopeUrl?: string | null;
}

const ZAPSIGN_HOST_SUFFIX = 'zapsign.com.br';

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Resolve o provider de um registro de assinatura.
 *
 * Ordem: `provider` explícito > host do `envelopeUrl` > `clicksign` (default
 * histórico — documentos criados antes da virada continuam na ClickSign).
 */
export function resolveSignatureProvider(source: SignatureEmbedSource): SignatureProviderId {
  const explicit = source.provider?.trim().toLowerCase();
  if (explicit === 'zapsign' || explicit === 'clicksign') {
    return explicit;
  }

  const host = source.envelopeUrl ? hostOf(source.envelopeUrl) : null;
  if (host && (host === ZAPSIGN_HOST_SUFFIX || host.endsWith(`.${ZAPSIGN_HOST_SUFFIX}`))) {
    return 'zapsign';
  }

  return 'clicksign';
}
